import { useState, useCallback, useEffect } from 'react'
import {
  Loader2, Search, AlertCircle, CheckCircle2, XCircle, AlertTriangle,
  Server, Globe, Shield, Link2, Wifi, WifiOff, Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  chainCheck, vpnStatus, ChainMode, ChainCheckResult, ChainModeResult,
  ChainStage, ChainGeo,
} from '../api'

const PROTO_OPTIONS = [
  { value: 'auto', label: 'Авто' },
  { value: 'http', label: 'HTTP' },
  { value: 'https', label: 'HTTPS' },
  { value: 'socks5', label: 'SOCKS5' },
  { value: 'socks4', label: 'SOCKS4' },
]

// The four legs of the chain. proxy/vpn_proxy need a proxy; vpn/vpn_proxy need
// an active VPN — the backend gates them too, but we reflect it in the UI.
const MODE_DEFS: { id: ChainMode; label: string; short: string; needsProxy: boolean; needsVpn: boolean; icon: typeof Server }[] = [
  { id: 'direct', label: 'Напрямую', short: 'Сервер → Яндекс', needsProxy: false, needsVpn: false, icon: Server },
  { id: 'vpn', label: 'Через VPN', short: 'Сервер → VPN → Яндекс', needsProxy: false, needsVpn: true, icon: Shield },
  { id: 'proxy', label: 'Через прокси', short: 'Сервер → прокси → Яндекс', needsProxy: true, needsVpn: false, icon: Globe },
  { id: 'vpn_proxy', label: 'VPN + прокси', short: 'Сервер → VPN → прокси → Яндекс', needsProxy: true, needsVpn: true, icon: Link2 },
]

function flagEmoji(cc?: string | null): string {
  if (!cc || cc.length !== 2) return '🌐'
  const A = 0x1F1E6
  const up = cc.toUpperCase()
  return String.fromCodePoint(A + up.charCodeAt(0) - 65) + String.fromCodePoint(A + up.charCodeAt(1) - 65)
}

const VERDICT: Record<string, { label: string; cls: string; dot: string }> = {
  ok: { label: 'Работает', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', dot: 'bg-emerald-400' },
  warn: { label: 'С оговоркой', cls: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30', dot: 'bg-yellow-400' },
  fail: { label: 'Не работает', cls: 'bg-red-500/15 text-red-400 border-red-500/30', dot: 'bg-red-400' },
}

function StageIcon({ status }: { status: ChainStage['status'] }) {
  if (status === 'ok') return <CheckCircle2 className="w-4 h-4 text-emerald-400" />
  if (status === 'warn') return <AlertTriangle className="w-4 h-4 text-yellow-400" />
  if (status === 'fail') return <XCircle className="w-4 h-4 text-red-400" />
  return <Info className="w-4 h-4 text-muted-foreground" />
}

function GeoLine({ geo }: { geo: ChainGeo }) {
  return (
    <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground pt-2 mt-2 border-t border-border/60">
      <span className="text-base leading-none">{flagEmoji(geo.country_code)}</span>
      {geo.ip && <code className="bg-black/30 px-1.5 py-0.5 rounded text-emerald-400 font-mono">{geo.ip}</code>}
      {geo.country && (
        <span className="text-foreground">
          {geo.country}{geo.region_name ? `, ${geo.region_name}` : ''}{geo.city ? `, ${geo.city}` : ''}
        </span>
      )}
      {geo.isp && <span>· {geo.isp}</span>}
      {geo.as && <span className="font-mono">· {geo.as}</span>}
      {geo.hosting && <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-amber-500/30 text-amber-400">hosting</Badge>}
      {geo.proxy && <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-red-500/30 text-red-400">proxy/vpn</Badge>}
      {geo.mobile && <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-blue-500/30 text-blue-400">mobile</Badge>}
    </div>
  )
}

function ModeCard({ result: r }: { result: ChainModeResult }) {
  if (!r.available) {
    return (
      <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 opacity-70">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-muted-foreground/40" />
          <span className="text-sm font-medium text-muted-foreground">{r.label}</span>
          <Badge variant="outline" className="ml-auto text-[10px] h-5 px-2 border-border text-muted-foreground">
            пропущено
          </Badge>
        </div>
        {r.skipped_reason && (
          <p className="text-xs text-muted-foreground mt-1.5 ml-4">{r.skipped_reason}</p>
        )}
      </div>
    )
  }
  const v = VERDICT[r.verdict] || VERDICT.fail
  return (
    <div className={`rounded-lg border overflow-hidden ${
      r.verdict === 'fail' ? 'border-red-500/25 bg-red-500/5'
        : r.verdict === 'warn' ? 'border-yellow-500/25 bg-yellow-500/5'
        : 'border-emerald-500/25 bg-emerald-500/5'
    }`}>
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border/60">
        <span className={`w-2 h-2 rounded-full ${v.dot}`} />
        <span className="text-sm font-semibold">{r.label}</span>
        <Badge variant="outline" className={`text-[10px] h-5 px-2 ${v.cls}`}>{v.label}</Badge>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-muted-foreground">
          {r.outer_ip && (
            <span className="hidden sm:inline">внешний IP: <code className="text-foreground font-mono">{r.outer_ip}</code></span>
          )}
          {r.elapsed_ms != null && <span>{(r.elapsed_ms / 1000).toFixed(1)}с</span>}
        </div>
      </div>
      <div className="px-4 py-3 space-y-2.5">
        {r.error && (
          <div className="text-xs text-red-400">Ошибка: {r.error}</div>
        )}
        {r.stages.map((s, i) => (
          <div key={i} className="flex items-start gap-2.5 text-xs">
            <div className="flex flex-col items-center pt-0.5">
              <StageIcon status={s.status} />
              {i < r.stages.length - 1 && <div className="w-px h-4 bg-border mt-1" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{s.label}</span>
                {s.ms != null && s.status !== 'fail' && (
                  <span className="text-[10px] text-muted-foreground font-mono">{s.ms} мс</span>
                )}
              </div>
              <div className={`text-[11px] mt-0.5 ${
                s.status === 'fail' ? 'text-red-400'
                  : s.status === 'warn' ? 'text-yellow-400/90'
                  : 'text-muted-foreground'
              }`}>{s.detail}</div>
            </div>
          </div>
        ))}
        {r.geo && <GeoLine geo={r.geo} />}
      </div>
    </div>
  )
}

export default function ChainChecker() {
  const [proxy, setProxy] = useState('')
  const [proto, setProto] = useState('auto')
  const [modes, setModes] = useState<Record<ChainMode, boolean>>({
    direct: true, vpn: true, proxy: true, vpn_proxy: true,
  })
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ChainCheckResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [vpnActive, setVpnActive] = useState<boolean | null>(null)
  const [vpnName, setVpnName] = useState<string>('')

  useEffect(() => {
    let alive = true
    vpnStatus().then((s: any) => {
      if (!alive) return
      setVpnActive(!!(s?.installed && s?.connected))
      setVpnName(s?.name || '')
    }).catch(() => { if (alive) setVpnActive(false) })
    return () => { alive = false }
  }, [])

  const hasProxy = proxy.trim().length > 0
  const selected = (Object.keys(modes) as ChainMode[]).filter(m => modes[m])

  const toggleMode = (m: ChainMode) => setModes(p => ({ ...p, [m]: !p[m] }))

  const handleCheck = useCallback(async () => {
    const chosen = (Object.keys(modes) as ChainMode[]).filter(m => modes[m])
    if (chosen.length === 0) return
    setLoading(true); setError(null); setResult(null)
    try {
      const res = await chainCheck(proxy.trim(), chosen, proto)
      setResult(res)
    } catch (e: any) {
      setError(e?.message || 'Ошибка проверки связки')
    } finally {
      setLoading(false)
    }
  }, [proxy, proto, modes])

  return (
    <div className="p-6 space-y-5 max-w-[1000px]">
      {/* Intro */}
      <div className="rounded-lg border border-border bg-card p-4 text-xs text-muted-foreground leading-relaxed space-y-1.5">
        <p className="text-foreground font-medium flex items-center gap-1.5">
          <Link2 className="w-4 h-4 text-primary" /> Проверка всей цепочки — с этого сервера
        </p>
        <p>
          Прогоняет реальный путь воркера прямо с вашего сервера: <b>Сервер → VPN → прокси → Яндекс</b>.
          В отличие от массового чекера (он оценивает прокси со стороннего сервиса), здесь трафик
          идёт тем же маршрутом, что у браузера — поэтому результат отражает, отдастся ли выдача на деле.
        </p>
      </div>

      {/* Proxy input */}
      <div className="space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[280px] space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Прокси <span className="text-muted-foreground/50">(нужен для режимов «через прокси»; для «Напрямую» и «Через VPN» можно оставить пустым)</span>
            </label>
            <input
              value={proxy}
              onChange={e => setProxy(e.target.value)}
              placeholder="socks5://user:pass@host:port  ·  host:port:user:pass  ·  http://host:port"
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-mono placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={loading}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Протокол</label>
            <div className="flex rounded-lg border border-border bg-background overflow-hidden">
              {PROTO_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setProto(opt.value)}
                  disabled={loading}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    proto === opt.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* VPN status hint */}
        <div className="flex items-center gap-2 text-xs">
          {vpnActive === null ? (
            <span className="text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> проверяю VPN…</span>
          ) : vpnActive ? (
            <span className="text-emerald-400 flex items-center gap-1.5">
              <Wifi className="w-3.5 h-3.5" /> VPN подключён{vpnName ? ` — ${vpnName}` : ''}
            </span>
          ) : (
            <span className="text-muted-foreground flex items-center gap-1.5">
              <WifiOff className="w-3.5 h-3.5" /> VPN не подключён — режимы «через VPN» будут пропущены
            </span>
          )}
        </div>

        {/* Mode chips */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {MODE_DEFS.map(md => {
            const Icon = md.icon
            const on = modes[md.id]
            const blockedProxy = md.needsProxy && !hasProxy
            const blockedVpn = md.needsVpn && vpnActive === false
            const blocked = blockedProxy || blockedVpn
            return (
              <button
                key={md.id}
                onClick={() => toggleMode(md.id)}
                disabled={loading}
                title={blockedProxy ? 'Укажите прокси' : blockedVpn ? 'VPN не подключён — этап будет пропущен' : ''}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all ${
                  on
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-border bg-background hover:bg-accent/40'
                } ${blocked && on ? 'opacity-70' : ''}`}
              >
                <span className={`flex items-center justify-center w-4 h-4 rounded border shrink-0 ${
                  on ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'
                }`}>
                  {on && <CheckCircle2 className="w-3 h-3" />}
                </span>
                <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-medium flex items-center gap-1.5">
                    {md.label}
                    {blocked && on && (
                      <span className="text-[10px] text-amber-400">
                        {blockedProxy ? '· нужен прокси' : '· нет VPN'}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1 truncate">
                    {md.short}
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="h-9 text-xs gap-1.5 px-5"
            onClick={handleCheck}
            disabled={loading || selected.length === 0}
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            {loading ? 'Проверяю связку…' : 'Проверить связку'}
          </Button>
          {loading && (
            <span className="text-[11px] text-muted-foreground">
              реальные запросы к Яндексу с сервера — до ~1 мин
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
            <h3 className="text-sm font-semibold text-foreground">Отчёт по этапам</h3>
            {result.proxy_masked && (
              <span>прокси: <code className="font-mono text-foreground">{result.proxy_masked}</code></span>
            )}
            <span className="flex items-center gap-1.5">
              {result.vpn.active
                ? <><Wifi className="w-3.5 h-3.5 text-emerald-400" /> VPN {result.vpn.name || 'активен'}{result.vpn.endpoint_host ? ` (${result.vpn.endpoint_host})` : ''}</>
                : <><WifiOff className="w-3.5 h-3.5" /> VPN не активен</>}
            </span>
          </div>

          <div className="space-y-2.5">
            {result.modes.map((m, i) => <ModeCard key={i} result={m} />)}
          </div>

          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-muted/30 border border-border text-[11px] text-muted-foreground leading-relaxed">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              <b>Как читать:</b> ICMP-пинг часто режется у провайдеров — если он не прошёл, но «Коннект» и «Выдача»
              зелёные, прокси рабочий. <b>Капча</b> на реальном запросе — это норма для не-браузера: она означает,
              что путь до Яндекса построен и он ответил. Красная «Выдача» (таймаут/нет ответа) — реальный обрыв цепочки.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
